import { Download } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { FormTextField } from "components/atoms";
import { ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { DialogHelper } from "helpers";
import { usePaginationRequest } from "hooks";
import { IDownloadCartonQRCodeBank } from "model";
import { useState } from "react";
import { DownloadApiService, MasterDataApiService } from "services";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";

interface IExportCartonQRCodeBank {
  numberToExport: number;
  label: string;
  level: number;
  email: string;
}
const validateSchema = yup.object().shape({
  numberToExport: yup
    .number()
    .min(1, "Số lượng phải lớn hơn 1")
    .required(translations.yupError.required),
  label: yup.string(),
  email: yup.string().email("Email không hợp lệ").required(translations.yupError.required),
});
const showDialog = () => {
    DialogHelper.open({
      title: "Thông báo",
      message: "Yêu cầu của bạn đã được tiếp nhận và đang được xử lý, sau khi hoàn tất bạn sẽ nhận được thông báo qua email."
    });
  };

const CartonQrCodeBank = () => {
  const [request] = useState({});
  const [refresh, setRefresh] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const {
    loading,
    result: { data },
  } = usePaginationRequest<any, any>(
    request,
    MasterDataApiService.getAvailableCartonBank,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const formik = useFormik<IExportCartonQRCodeBank>({
    initialValues: {
      numberToExport: 0,
      label: "",
      level: 1,
      email: ""
    },
    validationSchema: validateSchema,
    onSubmit: (
      values: IExportCartonQRCodeBank,
      { validateForm }: FormikHelpers<IExportCartonQRCodeBank>
    ) => {
      validateForm().then(() => {
        setDownloading(true);
        const params: IDownloadCartonQRCodeBank = {
          numberToExport: values.numberToExport,
          level: values.level,
          label: values.label,
          disableDownload: true,
          email: values.email
        };
        DownloadApiService.downloadCartonQRCodeBank(params)
          .then((response) => {
            if (response) {
              setDownloading(false);
              showDialog();
            }
          })
          .catch((error) => {
            setDownloading(false);
            DialogHelper.open({
              title: "Error",
              message: "An error occurred while downloading the data."
            });
          });
      });
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.qrCartonBank)}
      formik={formik}
      loading={downloading || loading}
      confirmButtonIcon={<Download />}
      confirmButtonText={"Export"}
    >
      <Grid
        container
        xs={3}
        item
        direction={"column"}
        sx={{ paddingRight: "10px" }}
      >
        <Box
          sx={{
            flex: 1,
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={"bold"}>
            {I18nTranslator(translations.receive.availableQuantity)}
          </Typography>
          <Typography fontWeight={"bold"}>
            {loading ? (
              <Skeleton variant="rectangular" width={120} height={20} />
            ) : (
              data?.totalAvailableQty?.toLocaleString()
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={"bold"}>
            {I18nTranslator(translations.receive.exportedQuantity)}
          </Typography>
          <Typography fontWeight={"bold"}>
            {loading ? (
              <Skeleton variant="rectangular" width={120} height={20} />
            ) : (
              data?.totalExportedQty?.toLocaleString()
            )}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            flexDirection: "row",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={"bold"}>
            {I18nTranslator(translations.receive.notExportedQuantity)}
          </Typography>
          <Typography fontWeight={"bold"}>
            {loading ? (
              <Skeleton variant="rectangular" width={120} height={20} />
            ) : (
              data?.totalNotExportedQty?.toLocaleString()
            )}
          </Typography>
        </Box>
      </Grid>
      <Grid container xs={9} item rowSpacing={2} sx={{ marginBottom: "10px" }}>
        <Grid xs={12} container item columnSpacing={2}>
          <Grid xs={2} item>
            <FormTextField
              id="numberToExport"
              label={I18nTranslator(translations.receive.exportQuantity)}
              placeholder={I18nTranslator(translations.placeholder.code)}
              type={"number"}
            />
          </Grid>
          <Grid xs={4} item>
            <FormTextField
              id="email"
              label={I18nTranslator(translations.masterData.email)}
              placeholder={I18nTranslator(translations.placeholder.email)}
              type={"email"}
            />
          </Grid>
          <Grid xs={6} item>
            <FormGroup sx={{ flexDirection: "row" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.level === 1}
                    onChange={() => formik.setFieldValue("level", 1)}
                  />
                }
                label={"Level 1"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.level === 2}
                    onChange={() => formik.setFieldValue("level", 2)}
                  />
                }
                label={"Level 2"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.level === 3}
                    onChange={() => formik.setFieldValue("level", 3)}
                  />
                }
                label={"Level 3"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.level === 4}
                    onChange={() => formik.setFieldValue("level", 4)}
                  />
                }
                label={"Level 4"}
              />
            </FormGroup>
          </Grid>
        </Grid>
        <Grid xs={12} item>
          <FormTextField
            id="label"
            label={I18nTranslator(translations.bank.label)}
            placeholder={I18nTranslator(
              translations.placeholder.inputTitleLabel
            )}
            multiline
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item rowSpacing={2} columnSpacing={2}>
        <CartonCard
          loading={loading}
          title={I18nTranslator(translations.bank.cartonLv1)}
          availableQty={data?.level1AvailableQty}
          exportedQty={data?.level1ExportedQty}
          notExportedQty={data?.level1NotExportedQty}
        />
        <CartonCard
          loading={loading}
          title={I18nTranslator(translations.bank.cartonLv2)}
          availableQty={data?.level2AvailableQty}
          exportedQty={data?.level2ExportedQty}
          notExportedQty={data?.level2NotExportedQty}
        />
        <CartonCard
          loading={loading}
          title={I18nTranslator(translations.bank.cartonLv3)}
          availableQty={data?.level3AvailableQty}
          exportedQty={data?.level3ExportedQty}
          notExportedQty={data?.level3NotExportedQty}
        />
        <CartonCard
          loading={loading}
          title={I18nTranslator(translations.bank.cartonLv4)}
          availableQty={data?.level4AvailableQty}
          exportedQty={data?.level4ExportedQty}
          notExportedQty={data?.level4NotExportedQty}
        />
      </Grid>
    </ReportHeader>
  );
};

const CartonCard = (props: {
  loading: boolean;
  title: string;
  availableQty?: string;
  exportedQty?: string;
  notExportedQty?: string;
}) => {
  const { loading, title, availableQty, exportedQty, notExportedQty } = props;
  return (
    <Grid container xs={3} item rowSpacing={2}>
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography sx={{ fontSize: 16 }} gutterBottom fontWeight={"bold"}>
            {title}
          </Typography>
          <Box
            sx={{
              flex: 1,
              flexDirection: "row",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography>
              {I18nTranslator(translations.receive.availableQuantity)}
            </Typography>
            <Typography fontWeight={"bold"}>
              {loading ? (
                <Skeleton variant="rectangular" width={120} height={20} />
              ) : (
                availableQty?.toLocaleString()
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              flexDirection: "row",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography>
              {I18nTranslator(translations.receive.exportedQuantity)}
            </Typography>
            <Typography fontWeight={"bold"}>
              {loading ? (
                <Skeleton variant="rectangular" width={120} height={20} />
              ) : (
                exportedQty?.toLocaleString()
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              flexDirection: "row",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography>
              {I18nTranslator(translations.receive.notExportedQuantity)}
            </Typography>
            <Typography fontWeight={"bold"}>
              {loading ? (
                <Skeleton variant="rectangular" width={120} height={20} />
              ) : (
                notExportedQty?.toLocaleString()
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default CartonQrCodeBank;
